import { useState, useEffect, useCallback, useRef } from 'react'
import { useParams, useNavigate, Link, useSearchParams } from 'react-router-dom'
import { useAuth } from '../../AuthProvider'
import TopNav from '../../components/TopNav'
import EmployeeLifeline from '../CustomerPortal/Lifeline/EmployeeLifeline'
import EmployeePicker from '../CustomerPortal/Lifeline/EmployeePicker'
import GeoMap from '../../components/GeoMap/GeoMap'
import CustomerAvatar from '../../components/CustomerAvatar'
import SolutionMatrix from '../../components/SolutionMatrix/SolutionMatrix'
import ContractView from '../../components/ContractView/ContractView'
import DrillPanel from '../CustomerProfile/DrillPanel'
import ProfileFilterBar from '../CustomerProfile/ProfileFilterBar'
import { fetchCustomerProfile, fetchProfileFilters, fetchGeoDrilldown, acknowledgeFinding, submitFixForFinding } from '../CustomerProfile/api'
import { STATUS_LABEL, statusOf, tabBadgeCount } from '../CustomerProfile/findings'
import { updateCustomer, executePinnedMetrics, deletePinnedMetric, getDashboard, listWorkEfforts, getPortalSettings, getCustomerSetup, finishCustomerSetup } from '../../api'
import { loadList } from '../../utils/loadState'
import SchemaCompare from '../schema-compare-v2'
import ComplianceTab from '../CustomerProfile/ComplianceTab'
import HealthTab from '../CustomerProfile/HealthTab'
import IntegrityTab from '../CustomerProfile/IntegrityTab'
import WorkEffortsTab from '../CustomerProfile/WorkEffortsTab'
import MetricsTab from '../CustomerProfile/MetricsTab'
import SpineView from '../CustomerProfile/SpineView'
import OverviewTab from '../CustomerProfile/OverviewTab'
import DataManagementTab from '../CustomerProfile/DataTab'
import RegistersTab from '../CustomerProfile/RegistersTab'
import '../CustomerProfile/CustomerProfile.css'
import '../CustomerProfile/DrillPanel.css'
import '../CustomerProfile/ProfileFilterBar.css'
import '../DataManagement/DataManagement.css'
import '../Registers/Registers.css'
import './CustomerPage.css'
import { API_BASE as API_BASE_CONFIG } from '../../config'
import { unitMedianSalaryText } from '../CustomerProfile/money'

// ─── Helpers ────────────────────────────────────────────
const BAR_COLORS = ['var(--green-light)', 'var(--blue-light)', 'var(--purple-light)', 'var(--amber-light)', 'var(--red-light)']

function fmtDate(iso) {
  if (!iso) return ''
  const d = new Date(iso)
  const now = new Date()
  const diff = now - d
  if (diff < 86400000) return `${Math.round(diff / 3600000)}h ago`
  if (diff < 604800000) return `${Math.round(diff / 86400000)}d ago`
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

function buildEffectiveFilters(activeFilters, filterDataResult) {
  if (!activeFilters) return activeFilters
  // Strip pay_date unconditionally — it's a Register/period filter, never
  // appropriate for profile or metric aggregate queries.
  const withoutPayDate = Object.fromEntries(
    Object.entries(activeFilters).filter(([k]) => k !== 'pay_date')
  )
  if (!filterDataResult?.filters) return withoutPayDate
  const effective = {}
  for (const [key, selectedValues] of Object.entries(withoutPayDate)) {
    if (!selectedValues || selectedValues.length === 0) continue
    const filterDef = filterDataResult.filters[key]
    if (!filterDef) { effective[key] = selectedValues; continue }
    const totalOptions = filterDef.options?.length || 0
    if (selectedValues.length >= totalOptions) continue
    effective[key] = selectedValues
  }
  return effective
}

// ─── Nav item definition ─────────────────────────────────
const NAV_ITEMS = [
  { key: 'overview',        label: 'Overview',        icon: '🏢', group: 'Analysis',   consultantOnly: false },
  { key: 'organization',   label: 'Organization',    icon: '🗂️', group: 'Analysis',   consultantOnly: false },
  { key: 'metrics',        label: 'Metrics',         icon: '📊', group: 'Analysis',   consultantOnly: false },
  { key: 'spine',          label: 'Backbone',        icon: '🧬', group: 'Analysis',   consultantOnly: false },
  { key: 'compliance',     label: 'Compliance',      icon: '⚖️', group: 'Analysis',   consultantOnly: false, badgeType: 'red' },
  { key: 'health',         label: 'Health',          icon: '🩺', group: 'Analysis',   consultantOnly: false, badgeType: 'amber' },
  { key: 'integrity',      label: 'Integrity',       icon: '🪜', group: 'Analysis',   consultantOnly: false },
  { key: 'system',         label: 'System',          icon: '⚙️', group: 'Analysis',   consultantOnly: false },
  { key: 'contract',       label: 'Contract',        icon: '📑', group: 'Analysis',   consultantOnly: false },
  // one person's record (queue item 219); shown with view_employee_detail
  { key: 'lifeline',       label: 'Employee Lifeline', icon: '🧭', group: 'Analysis', consultantOnly: false, permission: 'view_employee_detail' },
  { key: 'investigations', label: 'Investigations',  icon: '🔍', group: 'Consultant', consultantOnly: true },
  { key: 'data',           label: 'Data',            icon: '🗄️', group: 'Consultant', consultantOnly: true },
  { key: 'registers',      label: 'Registers',       icon: '📋', group: 'Consultant', consultantOnly: true },
  { key: 'compare',        label: 'Schema Compare',  icon: '🔀', group: 'Consultant', consultantOnly: true },
]

// ─── Observation (inline drillable) ──────────────────────
function Observation({ obs, customerId, onStatusChange }) {
  const [expanded, setExpanded] = useState(false)
  const [drillData, setDrillData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [drillError, setDrillError] = useState(null)
  const [showSQL, setShowSQL] = useState(false)
  const [exporting, setExporting] = useState(false)
  const [statusUpdating, setStatusUpdating] = useState(false)
  const [showOverridePanel, setShowOverridePanel] = useState(false)
  const [overrideType, setOverrideType] = useState('skip')
  const [overrideReason, setOverrideReason] = useState('')
  const [overrideParams, setOverrideParams] = useState('')

  const severityConfig = {
    risk: { icon: '!', cls: 'risk', label: 'Critical' },
    insight: { icon: '⚠', cls: 'insight', label: 'Warning' },
    note: { icon: 'ⓘ', cls: 'note', label: 'Info' },
  }
  const cfg = severityConfig[obs.type] || severityConfig.note

  const handleClick = async () => {
    if (!obs.id) return
    if (expanded) { setExpanded(false); return }
    setExpanded(true)
    if (drillData) return
    setLoading(true); setDrillError(null)
    try {
      const API = API_BASE_CONFIG
      const token = localStorage.getItem('xlr8_token') || ''
      const res = await fetch(`${API}/api/observations/${customerId}/drilldown/${obs.id}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      })
      if (res.ok) setDrillData(await res.json())
      else setDrillError(`HTTP ${res.status}`)
    } catch (e) { setDrillError(e.message || 'Network error') }
    setLoading(false)
  }

  const handleExport = async (e) => {
    e.stopPropagation()
    if (!drillData?.rows?.length) return
    setExporting(true)
    try {
      const cols = drillData.columns || Object.keys(drillData.rows[0])
      const csv = [cols.join(','), ...drillData.rows.map(r => cols.map(c => {
        const v = r[c] != null ? String(r[c]) : ''
        return v.includes(',') || v.includes('"') ? `"${v.replace(/"/g, '""')}"` : v
      }).join(','))].join('\n')
      const blob = new Blob([csv], { type: 'text/csv' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a'); a.href = url
      a.download = `${customerId}_observation_${obs.id?.slice(0, 8) || 'data'}.csv`
      a.click(); URL.revokeObjectURL(url)
    } catch (e) { console.error('Export failed:', e) }
    setExporting(false)
  }

  const handleStatus = async (status, e) => {
    e.stopPropagation()
    if (!obs.id || statusUpdating) return
    if (status === 'immaterial') { setShowOverridePanel(true); return }
    setStatusUpdating(true)
    try {
      // findings status v2: the action routes; nobody marks a finding resolved -- "fix
      // submitted", and the next scan on newer data verifies it
      const call = status === 'acknowledged' ? acknowledgeFinding : submitFixForFinding
      const result = await call(obs.id)
      if (result?.ok) { obs._localStatus = result.data?.status || status; onStatusChange?.() }
    } catch (e) { console.error('Status update failed:', e) }
    setStatusUpdating(false)
  }

  const currentStatus = statusOf({ status: obs._localStatus || obs.status })
  const isDismissed = currentStatus !== 'open'

  return (
    <div className={`cp-obs ${obs.id ? 'clickable' : ''} ${isDismissed ? 'dismissed' : ''}`} onClick={handleClick}>
      <div className={`cp-obs-icon ${cfg.cls}`}>{cfg.icon}</div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div className="cp-obs-title">
          {obs.title || ''}
          {isDismissed && <span className={`cp-obs-status-badge ${currentStatus}`}>{STATUS_LABEL[currentStatus] || currentStatus}</span>}
        </div>
        <div className="cp-obs-text">{obs.detail || ''}</div>
        {obs.source && <div className="cp-obs-meta">{obs.source}</div>}
        {expanded && (
          <div className="cp-obs-drill" onClick={e => e.stopPropagation()}>
            {loading && <div className="cp-obs-drill-loading">Loading data…</div>}
            {drillError && <div style={{ padding: '8px 0', fontSize: 12, color: 'var(--red)' }}>Failed to load: {drillError}</div>}
            {drillData?.rows?.length > 0 && (
              <>
                <div className="cp-obs-drill-table-wrap">
                  <table className="cp-obs-drill-table">
                    <thead><tr>{drillData.columns.map(c => <th key={c}>{c}</th>)}</tr></thead>
                    <tbody>
                      {drillData.rows.slice(0, 20).map((row, i) => (
                        <tr key={i}>{drillData.columns.map(c => <td key={c}>{row[c] != null ? String(row[c]) : '—'}</td>)}</tr>
                      ))}
                    </tbody>
                  </table>
                  {drillData.total_rows > 20 && <div className="cp-obs-drill-more">Showing 20 of {drillData.total_rows.toLocaleString()} rows</div>}
                </div>
                <div className="cp-obs-export-row">
                  <button className="cp-obs-export-btn" onClick={handleExport} disabled={exporting}>
                    {exporting ? 'Exporting…' : `↓ Export ${drillData.total_rows} rows`}
                  </button>
                  <button className="cp-obs-sql-toggle" onClick={e => { e.stopPropagation(); setShowSQL(!showSQL) }}>
                    {showSQL ? 'Hide SQL' : 'SQL'}
                  </button>
                </div>
              </>
            )}
            {showSQL && drillData?.sql && <div className="cp-obs-sql-block"><pre>{drillData.sql}</pre></div>}
            {drillData?.note && (!drillData.rows || drillData.rows.length === 0) && (
              <div className="cp-obs-drill-note">{drillData.note}</div>
            )}
            {!isDismissed && !showOverridePanel && obs.id && (
              <div className="cp-obs-actions">
                <button className="cp-obs-action-btn dismiss" onClick={e => handleStatus('immaterial', e)} disabled={statusUpdating}>Create Override</button>
                <button className="cp-obs-action-btn" onClick={e => handleStatus('acknowledged', e)} disabled={statusUpdating}>Acknowledge</button>
                <button className="cp-obs-action-btn resolve" onClick={e => handleStatus('fix_submitted', e)} disabled={statusUpdating}>Fix submitted</button>
              </div>
            )}
          </div>
        )}
      </div>
      {obs.id && <div className="cp-obs-chevron">{expanded ? '▾' : '›'}</div>}
    </div>
  )
}

// ─── Contract tab ───────────────────────────────────────
// The customer portal's Contract tab: same component, same call, same customer-scoped
// endpoints. What an admin sees beyond the customer (SQL, join checks, partition check)
// ContractView shows inline for admins only. An admin also gets one line saying whether
// this customer's own portal shows the tab -- it is off unless turned on per customer.
// A consultant sees what the customer sees, whether or not the customer's portal shows
// the tab yet (the server's tab switch gates the customer role only, routers/portal.py
// _require_tab); while it is off they get one line saying the customer can't see it.
function ContractTab({ customerId, companyName, isAdmin }) {
  const [portalTabs, setPortalTabs] = useState(null)
  const [settingsError, setSettingsError] = useState(null)

  useEffect(() => {
    let cancelled = false
    setPortalTabs(null); setSettingsError(null)
    getPortalSettings(customerId)
      .then(s => { if (!cancelled) setPortalTabs(s?.tabs || []) })
      .catch(e => { if (!cancelled) setSettingsError(e.message || 'request failed') })
    return () => { cancelled = true }
  }, [customerId])

  if (!isAdmin) {
    return (
      <>
        {portalTabs && !portalTabs.includes('contract') && (
          <div className="cpg-contract-note">
            <span className="cpg-contract-note-tag">Not shown to the customer</span>
            <span>{companyName}'s portal doesn't show the Contract tab yet, so they can't see this. An admin can turn it on.</span>
          </div>
        )}
        <ContractView customerId={customerId} customerName={companyName} embedded portal />
      </>
    )
  }

  return (
    <>
      <div className="cpg-contract-note">
        <span className="cpg-contract-note-tag">Admin only</span>
        <span>
          {settingsError ? `Couldn't read which tabs this customer's portal shows: ${settingsError}`
            : portalTabs == null ? "Checking this customer's portal…"
            : portalTabs.includes('contract') ? `${companyName}'s portal shows this Contract tab.`
            : `${companyName}'s portal does not show the Contract tab, so they can't see this yet. Turn it on from "Customer tabs…" in the portal preview.`}
        </span>
        <a className="cpg-contract-note-link" href={`/portal/${customerId}`} target="_blank" rel="noopener noreferrer">Preview Portal →</a>
      </div>
      <ContractView customerId={customerId} customerName={companyName} embedded portal />
    </>
  )
}

// ─── Finish setup ───────────────────────────────────────
// A customer whose context record exists without its customers row -- a create that
// failed half way. Syncs, uploads and metrics refuse it until the row is written, so the
// page says so and offers the one repair: POST /api/context/{id}/tenant-kind, which writes
// the row and keeps any kind already stored. Never "create it again", which would make a
// second customer.
function FinishSetupBanner({ customerId, onFinished }) {
  const [setup, setSetup] = useState(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)

  useEffect(() => {
    let cancelled = false
    setSetup(null); setError(null)
    getCustomerSetup(customerId)
      .then(s => { if (!cancelled) setSetup(s) })
      .catch(e => { if (!cancelled) console.warn('Customer setup state not available:', e) })
    return () => { cancelled = true }
  }, [customerId])

  if (!setup || setup.setup_finished) return null

  async function finish() {
    setBusy(true); setError(null)
    try {
      setSetup({ ...setup, ...(await finishCustomerSetup(customerId)), setup_finished: true })
      // What the missing row refused (the pinned metrics) can load now.
      onFinished?.()
    }
    catch (e) { setError(e.message || 'the request failed') }
    finally { setBusy(false) }
  }

  return (
    <div className="cpg-contract-note" role="alert">
      <span className="cpg-contract-note-tag">Setup not finished</span>
      <span>
        {customerId} has a context record but no customer row, so syncs, uploads and metrics
        are refused. Finish setting up this customer to write the row.
        {error && <> Couldn't finish setup: {error}</>}
      </span>
      <button className="nav-btn primary cpg-contract-note-link" onClick={finish} disabled={busy}>
        {busy ? 'Finishing…' : 'Finish setup'}
      </button>
    </div>
  )
}

// ═══════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════
export default function CustomerPage() {
  const { customerId } = useParams()
  const navigate = useNavigate()
  const { isConsultant, isAdmin, profile: authProfile } = useAuth()
  const isConsultantView = isConsultant || isAdmin

  // ── Core state ──
  const [profile, setProfile] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [dashboard, setDashboard] = useState(null)
  const [workEfforts, setWorkEfforts] = useState([])

  // CustomerPage is always consultant view — customers use /portal/:customerId

  // ── Nav ──
  const [activeNav, setActiveNav] = useState('overview')

  // ── Employee Lifeline (queue item 219), the same component and API as the portal.
  // ?lifeline=<opaque reference> opens one person; the Employee Lifeline tab without one
  // opens the company -> employee picker. No employee number or company code in the URL.
  const [searchParams, setSearchParams] = useSearchParams()
  const permissions = new Set(authProfile?.permissions || [])
  const canViewEmployee = permissions.has('view_employee_detail')
  const lifelineFor = canViewEmployee ? searchParams.get('lifeline') : null
  useEffect(() => { if (lifelineFor) setActiveNav('lifeline') }, [lifelineFor])
  const openEmployee = useCallback((employeeRef) => {
    if (!employeeRef) return
    setSearchParams({ lifeline: String(employeeRef) })
    setActiveNav('lifeline')
    window.scrollTo?.(0, 0)
  }, [setSearchParams])
  const closeEmployee = useCallback(() => { setSearchParams({}); setActiveNav('overview') }, [setSearchParams])
  const pickAnother = useCallback(() => { setSearchParams({}); setActiveNav('lifeline') }, [setSearchParams])
  const onOpenEmployee = canViewEmployee ? openEmployee : null

  // ── Org state ──
  const [selectedDiv, setSelectedDiv] = useState(null)
  const [expandedDivs, setExpandedDivs] = useState(new Set())
  const [orgSearch, setOrgSearch] = useState('')
  const [orgView, setOrgView] = useState('location')
  const [selectedLocation, setSelectedLocation] = useState(null)

  // ── System toggle ──
  const [activeSystemId, setActiveSystemId] = useState(null)
  const [activeMetricModule, setActiveMetricModule] = useState(null)
  const [systems, setSystems] = useState([])

  // ── Misc ──
  const [collapsedSections, setCollapsedSections] = useState(new Set())
  const [assignedCodes, setAssignedCodes] = useState([])
  const [savingCodes, setSavingCodes] = useState(false)
  const [pinnedMetrics, setPinnedMetrics] = useState([])
  const [drillPanel, setDrillPanel] = useState(null)
  // A failed pinned-metrics read is an error with a Retry on the Overview, never a
  // section that silently isn't there (item 201).
  const [pinnedError, setPinnedError] = useState(null)
  const loadPinned = useCallback(async () => {
    const { items, error } = await loadList(() => executePinnedMetrics(customerId))
    setPinnedMetrics(items || [])
    setPinnedError(error)
  }, [customerId])

  // ── Filters ──
  const [filterData, setFilterData] = useState(null)
  const [activeFilters, setActiveFilters] = useState({})
  const [filtersLoaded, setFiltersLoaded] = useState(false)
  const [profileRefreshing, setProfileRefreshing] = useState(false)
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false)
  const filterChangeRef = useRef(0)

  // ── Load everything ──
  useEffect(() => {
    let cancelled = false
    async function load() {
      try {
        setLoading(true)
        // Reset filter state immediately so stale filters from the previous
        // customer never reach MetricsTab while the new customer's filters load.
        setFilterData(null)
        setActiveFilters({})
        setFiltersLoaded(false)

        // Filters
        let filtersResult = null
        try { filtersResult = await fetchProfileFilters(customerId) }
        catch (e) { console.warn('Filters not available:', e) }

        if (cancelled) return

        let defaults = {}
        if (filtersResult?.filters) {
          setFilterData(filtersResult)
          for (const [key, filterDef] of Object.entries(filtersResult.filters)) {
            if (filterDef.default) {
              const available = new Set(filterDef.options.map(o => o.value))
              defaults[key] = filterDef.default.filter(d => available.has(d))
            } else {
              defaults[key] = filterDef.options.map(o => o.value)
            }
          }
          setActiveFilters(defaults)
        }
        setFiltersLoaded(true)

        const effectiveDefaults = buildEffectiveFilters(defaults, filtersResult)

        // Load profile, dashboard, work efforts in parallel
        const [profileData, dashData, effortsData] = await Promise.allSettled([
          fetchCustomerProfile(customerId, effectiveDefaults, null),
          getDashboard(customerId),
          listWorkEfforts(customerId),
        ])

        if (cancelled) return

        if (profileData.status === 'fulfilled') {
          const data = profileData.value
          if (data) {
            setProfile(data)
            setAssignedCodes(data.identity?.company_codes || [])
            const sysList = data.identity?.systems || []
            setSystems(sysList)
            if (sysList.length > 0) {
              const primary = sysList.find(s => s.is_primary) || sysList[0]
              if (primary) setActiveSystemId(primary.id)
            }
            if (data.org_tree?.length > 0) {
              setSelectedDiv(data.org_tree[0].name)
              setExpandedDivs(new Set([data.org_tree[0].name]))
            }
            loadPinned()
          }
        }
        if (dashData.status === 'fulfilled') setDashboard(dashData.value)
        if (effortsData.status === 'fulfilled') {
          const d = effortsData.value
          setWorkEfforts(Array.isArray(d) ? d : (d?.work_efforts || []))
        }
      } catch (err) {
        if (!cancelled) setError(err.message)
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => { cancelled = true }
  }, [customerId])

  const handleFiltersChange = useCallback(async (newFilters) => {
    setActiveFilters(newFilters)
    const changeId = ++filterChangeRef.current
    setProfileRefreshing(true)
    try {
      const effective = buildEffectiveFilters(newFilters, filterData)
      const data = await fetchCustomerProfile(customerId, effective, activeSystemId)
      if (changeId === filterChangeRef.current) {
        if (data) {
          setProfile(data)
          if (data.org_tree?.length > 0 && !data.org_tree.find(d => d.name === selectedDiv)) {
            setSelectedDiv(data.org_tree[0].name)
            setExpandedDivs(new Set([data.org_tree[0].name]))
          }
        }
      }
    } catch (err) { console.error('Profile refresh failed:', err) }
    finally { if (changeId === filterChangeRef.current) setProfileRefreshing(false) }
  }, [customerId, selectedDiv, filterData, activeSystemId])

  const handleSystemChange = useCallback(async (systemId) => {
    setActiveSystemId(systemId)
    setProfileRefreshing(true)
    try {
      const effective = buildEffectiveFilters(activeFilters, filterData)
      const data = await fetchCustomerProfile(customerId, effective, systemId)
      if (data) setProfile(data)
    } catch (err) { console.error('System switch failed:', err) }
    finally { setProfileRefreshing(false) }
  }, [customerId, activeFilters, filterData])

  const toggleDiv = useCallback((divName) => {
    setExpandedDivs(prev => {
      const next = new Set(prev)
      next.has(divName) ? next.delete(divName) : next.add(divName)
      return next
    })
    setSelectedDiv(divName)
  }, [])

  const toggleSection = (name) => {
    setCollapsedSections(prev => {
      const next = new Set(prev)
      next.has(name) ? next.delete(name) : next.add(name)
      return next
    })
  }

  async function toggleCompanyCode(code) {
    const prev = assignedCodes
    const next = assignedCodes.includes(code)
      ? assignedCodes.filter(c => c !== code)
      : [...assignedCodes, code]
    // Every customer-context change records why (the server records who and when).
    const reason = window.prompt(`Why is company code ${code} ${prev.includes(code) ? 'being removed' : 'being added'}?`, '')
    if (reason === null) return          // cancelled
    if (!reason.trim()) {
      window.alert(`Company code ${code} was not changed: a reason is required.`)
      return
    }
    setAssignedCodes(next)
    setSavingCodes(true)
    try { await updateCustomer(customerId, { company_codes: next, reason: reason.trim() }) }
    catch (e) {
      console.error('Failed to save company codes:', e)
      setAssignedCodes(prev)
      window.alert(`Company code ${code} was not saved: ${e.message || 'the save failed'}`)
    }
    setSavingCodes(false)
  }

  const handleNavClick = (key) => {
    if (lifelineFor) setSearchParams({})
    setActiveNav(key)
  }
  const visibleNavItems = NAV_ITEMS.filter(item => item.key !== 'investigations'
    && (!item.permission || permissions.has(item.permission)))

  // ── Loading / error ──
  if (loading) {
    return (
      <div className="cp-root">
        <TopNav breadcrumbs={[{ label: 'Customers', to: '/customers' }, { label: 'Loading…' }]} />
        <div className="cp-page"><div className="cp-loading"><div className="dm-spinner" style={{ width: 28, height: 28, marginRight: 12 }} /> Loading…</div></div>
      </div>
    )
  }
  if (error) {
    return (
      <div className="cp-root">
        <TopNav breadcrumbs={[{ label: 'Customers', to: '/customers' }, { label: 'Error' }]} />
        <div className="cp-page"><div className="cp-error">Failed to load: {error}</div></div>
      </div>
    )
  }
  if (!profile) {
    // New customer — no data tables yet. Show full page shell with Data tab active.
    return (
      <div className="cpg-root">
        <TopNav
          breadcrumbs={[{ label: 'Customers', to: '/customers' }, { label: customerId }]}
          actions={<><Link to={`/customers/${customerId}/edit`} className="nav-btn">Edit</Link></>}
        />
        <div className="cpg-tab-row">
          <nav className="cpg-tabs">
            <div className="cpg-tab active">📁 Data</div>
          </nav>
        </div>
        <div className="cpg-body">
          <main className="cpg-main">
            <div className="cpg-content">
              <div className="cpg-content-inner">
                {isConsultantView && <FinishSetupBanner customerId={customerId} onFinished={loadPinned} />}
                <div style={{ background: 'var(--green-subtle)', border: '1px solid #a9d6fb', borderRadius: 10, padding: '18px 24px', marginBottom: 24, display: 'flex', alignItems: 'center', gap: 14 }}>
                  <div style={{ fontSize: 28 }}>🎉</div>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--green-dark)' }}>Customer created — upload data to get started</div>
                    <div style={{ fontSize: 12.5, color: 'var(--gray-500)', marginTop: 3 }}>Upload a CSV or Excel file below. Once processed, all tabs will populate automatically.</div>
                  </div>
                </div>
                <DataManagementTab customerId={customerId} />
              </div>
            </div>
          </main>
        </div>
      </div>
    )
  }

  const { identity, kpis, org_tree, work_locations, geography, geo_type, composition, observations, data_freshness, system_configuration, complexity, drilldown: drilldownMap } = profile
  const drilldownFilters = profile.drilldown_filters || {}
  const companyName = identity?.company_name || customerId


  // Has active (non-default) filters
  const hasActiveFilters = filterData ? Object.keys(filterData.filters || {}).some(key => {
    const sel = activeFilters[key] || []
    const opts = filterData.filters[key]?.options || []
    const defs = filterData.filters[key]?.default || []
    if (defs.length > 0) { const ds = new Set(defs), ss = new Set(sel); if (ds.size !== ss.size) return true; for (const d of ds) if (!ss.has(d)) return true; return false }
    return sel.length > 0 && sel.length < opts.length
  }) : false

  // Dashboard data
  const m = dashboard?.metrics || {}
  const depts = dashboard?.departments || []
  const comp = dashboard?.compensation || {}

  // Findings from work efforts
  let critFindings = 0, warnFindings = 0
  workEfforts.forEach(we => {
    (we.findings || []).forEach(f => {
      const sev = f.severity || f.type || ''
      if (sev === 'critical') critFindings++
      else if (sev === 'warning') warnFindings++
    })
  })
  const totalFindings = workEfforts.reduce((a, we) => a + (we.findings?.length || 0), 0)

  const selectedDivData = org_tree?.find(d => d.name === selectedDiv)

  const openChartDrill = (chartId, item, filterKeyOverride) => {
    const queryId = drilldownMap?.[chartId]
    if (!queryId) return
    const filterKey = filterKeyOverride || drilldownFilters[chartId]
    const filterValue = item.range ? item.range : (item.code !== undefined ? item.code : item.label)
    setDrillPanel({
      queryId,
      filters: filterKey && filterValue !== undefined ? { [filterKey]: filterValue } : null,
      title: item.label || String(filterValue),
      subtitle: `${item.count?.toLocaleString()} employees`,
    })
  }

  const breadcrumbs = [
    { label: 'Customers', to: '/customers' },
    { label: companyName },
  ]

  const navActions = (
    <>
      {isConsultantView && (
        <Link to={`/customers/${customerId}/edit`} className="nav-btn">Edit</Link>
      )}
      {isConsultantView && (
        <a className="nav-btn" href={`/portal/${customerId}`} target="_blank" rel="noopener noreferrer">
          Preview Portal →
        </a>
      )}
      {isConsultantView && (
        <button className="nav-btn primary" onClick={() => navigate(`/customers/${customerId}/work-efforts/new`)}>
          ⚡ New Work Effort
        </button>
      )}
    </>
  )

  return (
    <div className="cpg-root">

      {/* ── GLOBAL TOP NAV ── */}
      <TopNav breadcrumbs={breadcrumbs} actions={navActions} />

      {/* ── CUSTOMER TAB ROW ── */}
      <div className="cpg-tab-row">
        <nav className="cpg-tabs">
          {visibleNavItems.map(item => {
            // a findings tab's badge is its own Open count (findings.tabFindingCounts), nothing else
            const badge = tabBadgeCount(observations, item.key)
            const countBadge = item.key === 'investigations' ? workEfforts.length : 0
            return (
              <div
                key={item.key}
                className={`cpg-tab ${activeNav === item.key ? 'active' : ''}`}
                onClick={() => handleNavClick(item.key)}
              >
                {item.icon && <span className="cpg-tab-icon">{item.icon}</span>}
                {item.label}
                {badge > 0 && <span className={`cpg-tab-badge ${item.badgeType}`}>{badge}</span>}
                {countBadge > 0 && <span className="cpg-tab-badge cnt">{countBadge}</span>}
              </div>
            )
          })}
        </nav>
      </div>

      {/* ── FILTER DRAWER ── (Metrics + Backbone views) */}
      {(activeNav === 'metrics' || activeNav === 'spine') && filtersLoaded && filterData && (
        <>
          {/* Right-edge pull tab */}
          <button
            className={`cpg-filter-pull-tab ${filterDrawerOpen ? 'open' : ''}`}
            onClick={() => setFilterDrawerOpen(v => !v)}
            title="Filters"
          >
            <span className="cpg-filter-pull-icon">⏣</span>
            <span className="cpg-filter-pull-label">Filters</span>
            {hasActiveFilters && <span className="cpg-filter-pull-dot" />}
          </button>

          {/* Drawer */}
          <div className={`cpg-filter-drawer ${filterDrawerOpen ? 'open' : ''}`}>
            <div className="cpg-filter-drawer-head">
              <span className="cpg-filter-drawer-title">⏣ Filters</span>
              <button className="cpg-filter-drawer-close" onClick={() => setFilterDrawerOpen(false)}>✕</button>
            </div>
            <div className="cpg-filter-drawer-body">
              <ProfileFilterBar
                customerId={customerId}
                filterData={filterData}
                activeFilters={activeFilters}
                onFiltersChange={handleFiltersChange}
                inlineMode={true}
                activeTab="workforce"
                activeMetricModule={activeMetricModule}
              />
            </div>
          </div>
          {filterDrawerOpen && <div className="cpg-filter-drawer-backdrop" onClick={() => setFilterDrawerOpen(false)} />}
        </>
      )}

      {/* ── BODY ── */}
      <div className="cpg-body">
        <main className="cpg-main">
          <div className="cpg-content">
          <div className="cpg-content-inner">

            {isConsultantView && <FinishSetupBanner customerId={customerId} onFinished={loadPinned} />}

            {/* ── IDENTITY STRIP ── */}
            <div className="cpg-identity-strip">
              <CustomerAvatar
                customerId={customerId}
                companyName={identity?.company_name}
                website={identity?.website}
                size="xl"
              />
              <div className="cpg-identity-info">
                <div className="cpg-identity-name">{companyName}</div>
                <div className="cpg-identity-meta">
                  {[identity?.industry, identity?.hcm_product || identity?.hcm_vendor].filter(Boolean).join(' · ')}
                </div>
              </div>
            </div>

            {/* ── OVERVIEW ── */}
            {activeNav === 'overview' && (
              <>
                <OverviewTab
                  customerId={customerId}
                  kpis={kpis}
                  observations={observations}
                  data_freshness={data_freshness}
                  pinnedMetrics={pinnedMetrics}
                  pinnedError={pinnedError}
                  onRetryPinned={loadPinned}
                  setPinnedMetrics={setPinnedMetrics}
                  onTabSwitch={(tab) => setActiveNav(tab)}
                  deletePinnedMetric={deletePinnedMetric}
                  composition={composition}
                  geography={geography}
                  geo_type={geo_type}
                  customerMode={false}
                />

              </>
            )}

            {/* ── ORGANIZATION ── */}
            {activeNav === 'organization' && (
              <>
                {geography && geography.length > 0 && (
                  <div className="cp-section">
                    <div className="cp-section-header cp-section-toggle" onClick={() => toggleSection('geo')}>
                      <div>
                        <div className="cp-section-title">
                          <span className="cp-section-chevron">{collapsedSections.has('geo') ? '▸' : '▾'}</span>
                          Geographic Distribution
                        </div>
                        <div className="cp-section-subtitle">{geography.length} {geography.every(g => /^[A-Z]{2}$/.test(g.state)) ? 'states' : 'locations'}</div>
                      </div>
                    </div>
                    {!collapsedSections.has('geo') && (
                      <GeoMap geography={geography} geoType={profile?.geo_type} customerId={customerId} fetchDrilldown={fetchGeoDrilldown} workLocations={work_locations} />
                    )}
                  </div>
                )}

                {org_tree && org_tree.length > 0 && (
                  <div className="cp-section">
                    <div className="cp-section-header cp-section-toggle" onClick={() => toggleSection('org')}>
                      <div>
                        <div className="cp-section-title">
                          <span className="cp-section-chevron">{collapsedSections.has('org') ? '▸' : '▾'}</span>
                          Organizational Structure
                        </div>
                        <div className="cp-section-subtitle">Click any level to see headcount and comp</div>
                      </div>
                      {!collapsedSections.has('org') && (
                        <div className="cp-section-tabs">
                          <button className={`cp-tab ${orgView === 'division' ? 'active' : ''}`} onClick={e => { e.stopPropagation(); setOrgView('division') }}>Division → Dept</button>
                          <button className={`cp-tab ${orgView === 'location' ? 'active' : ''}`} onClick={e => { e.stopPropagation(); setOrgView('location') }}>Location</button>
                        </div>
                      )}
                    </div>

                    {!collapsedSections.has('org') && orgView === 'division' && (
                      <div className="cp-org-grid">
                        <div className="cp-org-tree">
                          <div className="cp-org-tree-header">
                            <span>Org Tree</span>
                            <span style={{ fontSize: 10, fontWeight: 400 }}>{org_tree.length} divisions</span>
                          </div>
                          <div className="cp-org-search">
                            <input type="text" placeholder="Search divisions…" value={orgSearch} onChange={e => setOrgSearch(e.target.value)} />
                          </div>
                          <div className="cp-org-list">
                            {org_tree.filter(d => !orgSearch || d.name.toLowerCase().includes(orgSearch.toLowerCase())).map(div => (
                              <div key={div.name}>
                                <div className={`cp-org-node l0 ${selectedDiv === div.name ? 'active' : ''}`} onClick={() => toggleDiv(div.name)}>
                                  <span className="cp-org-expand">{expandedDivs.has(div.name) && div.departments?.length > 0 ? '▾' : '▸'}</span>
                                  <span className="cp-org-label">{div.name}</span>
                                  <span className="cp-org-count">{div.headcount}</span>
                                </div>
                                {expandedDivs.has(div.name) && div.departments?.map(dept => (
                                  <div key={dept.name} className="cp-org-node l1">
                                    <span className="cp-org-expand" />
                                    <span className="cp-org-label">{dept.name}</span>
                                    <span className="cp-org-count">{dept.headcount}</span>
                                  </div>
                                ))}
                              </div>
                            ))}
                          </div>
                        </div>
                        <div className="cp-org-detail">
                          {selectedDivData ? (
                            <>
                              <div className="cp-org-detail-header">
                                <div className="cp-org-detail-title">{selectedDivData.name}</div>
                                <div className="cp-org-detail-path">{companyName} → {selectedDivData.name} · {(selectedDivData.headcount / kpis.total_headcount * 100).toFixed(0)}% of company</div>
                              </div>
                              <div className="cp-org-detail-kpis">
                                <div className="cp-org-kpi"><div className="cp-org-kpi-value">{selectedDivData.headcount}</div><div className="cp-org-kpi-label">Headcount</div></div>
                                <div className="cp-org-kpi"><div className="cp-org-kpi-value" style={{ color: 'var(--blue)' }}>{unitMedianSalaryText(selectedDivData)}</div><div className="cp-org-kpi-label">Median Salary</div></div>
                                <div className="cp-org-kpi"><div className="cp-org-kpi-value">{selectedDivData.state_count || '—'}</div><div className="cp-org-kpi-label">States</div></div>
                                <div className="cp-org-kpi"><div className="cp-org-kpi-value">{selectedDivData.departments?.length || 0}</div><div className="cp-org-kpi-label">Departments</div></div>
                              </div>
                              <div className="cp-org-detail-body">
                                {selectedDivData.departments?.length > 0 && (
                                  <>
                                    <div className="cp-obs-cat">Departments in {selectedDivData.name}</div>
                                    {selectedDivData.departments.map((dept, i) => (
                                      <div key={dept.name} className="cp-bar" style={{ padding: '6px 0' }}>
                                        <span className="cp-bar-label">{dept.name}</span>
                                        <div className="cp-bar-track"><div className="cp-bar-fill" style={{ width: `${dept.headcount / selectedDivData.headcount * 100}%`, background: BAR_COLORS[i % BAR_COLORS.length] }} /></div>
                                        <span className="cp-bar-value">{dept.headcount} <span className="cp-bar-pct">{(dept.headcount / selectedDivData.headcount * 100).toFixed(0)}%</span></span>
                                      </div>
                                    ))}
                                  </>
                                )}
                              </div>
                            </>
                          ) : (
                            <div style={{ padding: 40, textAlign: 'center', color: 'var(--gray-400)', fontSize: 13 }}>Select a division</div>
                          )}
                        </div>
                      </div>
                    )}

                    {!collapsedSections.has('org') && orgView === 'location' && work_locations?.length > 0 && (() => {
                      const filteredLocs = work_locations.filter(wl => !orgSearch || wl.description?.toLowerCase().includes(orgSearch.toLowerCase()) || wl.code?.toLowerCase().includes(orgSearch.toLowerCase()))
                      const selCode = selectedLocation || work_locations[0]?.code
                      const selLoc = work_locations.find(wl => wl.code === selCode) || work_locations[0]
                      const maxLocCount = work_locations[0]?.employee_count || 1
                      return (
                        <div className="cp-org-grid">
                          <div className="cp-org-tree">
                            <div className="cp-org-tree-header"><span>Work Locations</span><span style={{ fontSize: 10, fontWeight: 400 }}>{work_locations.length} locations</span></div>
                            <div className="cp-org-search"><input type="text" placeholder="Search locations…" value={orgSearch} onChange={e => setOrgSearch(e.target.value)} /></div>
                            <div className="cp-org-list">
                              {filteredLocs.map(wl => (
                                <div key={wl.code} className={`cp-org-node l0 ${selCode === wl.code ? 'active' : ''}`} onClick={() => setSelectedLocation(wl.code)}>
                                  <span className="cp-org-expand" />
                                  <span className="cp-org-label"><span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, fontWeight: 600, color: 'var(--gray-500)', marginRight: 6 }}>{wl.code}</span>{wl.description || ''}</span>
                                  <span className="cp-org-count">{wl.employee_count}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                          <div className="cp-org-detail">
                            {selLoc && (
                              <>
                                <div className="cp-org-detail-header">
                                  <div className="cp-org-detail-title">{selLoc.description || selLoc.code}</div>
                                  <div className="cp-org-detail-path">Code: {selLoc.code}{selLoc.city ? ` · ${selLoc.city}` : ''}{selLoc.state ? `, ${selLoc.state}` : ''} · {(selLoc.employee_count / kpis.total_headcount * 100).toFixed(1)}% of workforce</div>
                                </div>
                                <div className="cp-org-detail-kpis">
                                  <div className="cp-org-kpi"><div className="cp-org-kpi-value">{selLoc.employee_count.toLocaleString()}</div><div className="cp-org-kpi-label">Headcount</div></div>
                                  <div className="cp-org-kpi"><div className="cp-org-kpi-value" style={{ color: 'var(--blue)' }}>{(selLoc.employee_count / kpis.total_headcount * 100).toFixed(1)}%</div><div className="cp-org-kpi-label">% of Total</div></div>
                                  <div className="cp-org-kpi"><div className="cp-org-kpi-value">#{work_locations.indexOf(selLoc) + 1}</div><div className="cp-org-kpi-label">Rank</div></div>
                                </div>
                                <div className="cp-org-detail-body">
                                  <div className="cp-obs-cat">Top Locations</div>
                                  {work_locations.slice(0, 20).map((wl, i) => (
                                    <div key={wl.code} className="cp-bar" style={{ padding: '5px 0' }}>
                                      <span className="cp-bar-label" style={{ fontWeight: wl.code === selLoc.code ? 700 : 400 }}><span style={{ fontFamily: 'var(--font-mono)', fontSize: 10, opacity: 0.6, marginRight: 4 }}>{wl.code}</span>{wl.description || wl.code}</span>
                                      <div className="cp-bar-track"><div className="cp-bar-fill" style={{ width: `${wl.employee_count / maxLocCount * 100}%`, background: wl.code === selLoc.code ? 'var(--blue)' : BAR_COLORS[i % BAR_COLORS.length] }} /></div>
                                      <span className="cp-bar-value">{wl.employee_count.toLocaleString()}</span>
                                    </div>
                                  ))}
                                </div>
                              </>
                            )}
                          </div>
                        </div>
                      )
                    })()}
                  </div>
                )}
              </>
            )}

            {/* ── METRICS ── */}
            {activeNav === 'metrics' && (
              <MetricsTab
                customerId={customerId}
                companyName={companyName}
                activeFilters={activeFilters}
                filterData={filterData}
                consultantMode={true}
                onModuleChange={setActiveMetricModule}
                onOpenEmployee={onOpenEmployee}
              />
            )}

            {/* ── SPINE (§8 operating-model spine — Enterprise slice) ── */}
            {activeNav === 'spine' && (
              <SpineView
                customerId={customerId}
                companyName={companyName}
                logoBase64={identity?.logo_base64}
                activeFilters={activeFilters}
                filterData={filterData}
              />
            )}

            {/* ── COMPLIANCE ── */}
            {activeNav === 'compliance' && (
              <ComplianceTab customerId={customerId} observations={observations} onOpenEmployee={onOpenEmployee} />
            )}

            {/* ── HEALTH ── */}
            {activeNav === 'health' && (
              <HealthTab customerId={customerId} observations={observations} onOpenEmployee={onOpenEmployee} />
            )}

            {/* ── INTEGRITY (§STEP 6 — the ladder: rails leak forward, rungs mean the stacks disagree) ── */}
            {activeNav === 'integrity' && (
              <IntegrityTab customerId={customerId} />
            )}

            {/* ── SYSTEM ── */}
            {activeNav === 'system' && (
              <>
                {/* System selector */}
                {systems.length > 0 && (
                  <div className="cp-section">
                    <div className="cp-section-header">
                      <div>
                        <div className="cp-section-title">Viewing</div>
                        <div className="cp-section-subtitle">Switch between connected systems</div>
                      </div>
                    </div>
                    <div className="cpg-sys-inline-list">
                      {systems.map(sys => (
                        <div
                          key={sys.id}
                          className={`cpg-sys-inline-opt ${activeSystemId === sys.id ? 'active' : ''}`}
                          onClick={() => handleSystemChange(sys.id)}
                        >
                          <span className={`cpg-sys-dot ${sys.status === 'active' ? 'active' : 'decom'}`} />
                          {sys.product_name || sys.vendor_name}
                        </div>
                      ))}
                      {systems.length > 1 && (
                        <div
                          className={`cpg-sys-inline-opt ${!activeSystemId ? 'active' : ''}`}
                          onClick={() => handleSystemChange(null)}
                        >
                          <span className="cpg-sys-dot all" /> All Systems
                        </div>
                      )}
                      {profileRefreshing && <span className="cpg-sys-loading">Updating…</span>}
                    </div>
                  </div>
                )}

                {system_configuration && (
                  system_configuration.companies?.length > 0 ||
                  system_configuration.org_levels?.length > 0 ||
                  system_configuration.earning_groups?.length > 0 ||
                  system_configuration.deduction_groups?.length > 0
                ) && (
                  <div className="cp-section">
                    <div className="cp-section-header">
                      <div>
                        <div className="cp-section-title">System Configuration</div>
                        <div className="cp-section-subtitle">Companies, org levels, earning & deduction groups</div>
                      </div>
                    </div>
                    <div className="cp-sysconfig-grid">
                      {system_configuration.companies?.length > 0 && (
                        <div className="cp-sysconfig-card">
                          <div className="cp-sysconfig-card-header">
                            <span className="cp-sysconfig-card-icon" style={{ background: 'var(--blue-light)', color: 'var(--blue)' }}>🏢</span>
                            <div>
                              <div className="cp-sysconfig-card-title">Companies</div>
                              <div className="cp-sysconfig-card-count">{system_configuration.companies.length} companies</div>
                            </div>
                          </div>
                          <div className="cp-sysconfig-list">
                            {system_configuration.companies.map((c, i) => (
                              <div key={i} className={`cp-sysconfig-row ${assignedCodes.includes(c.code) ? 'cp-code-assigned' : ''}`}>
                                <label className="cp-code-check">
                                  <input type="checkbox" checked={assignedCodes.includes(c.code)} onChange={() => toggleCompanyCode(c.code)} />
                                </label>
                                <span className="cp-sysconfig-code">{c.code}</span>
                                <span className="cp-sysconfig-name">{c.name}</span>
                                {c.employee_count != null && <span className="cp-sysconfig-count">{c.employee_count.toLocaleString()}</span>}
                              </div>
                            ))}
                            {savingCodes && <div className="cp-code-saving">Saving…</div>}
                          </div>
                        </div>
                      )}
                      {system_configuration.org_levels?.length > 0 && (
                        <div className="cp-sysconfig-card">
                          <div className="cp-sysconfig-card-header">
                            <span className="cp-sysconfig-card-icon" style={{ background: 'var(--green-light)', color: 'var(--green-dark)' }}>🏗️</span>
                            <div>
                              <div className="cp-sysconfig-card-title">Org Levels</div>
                              <div className="cp-sysconfig-card-count">{system_configuration.org_levels.length} levels configured</div>
                            </div>
                          </div>
                          <div className="cp-sysconfig-list">
                            {system_configuration.org_levels.map((ol, i) => (
                              <div key={i} className="cp-sysconfig-org-level">
                                <div className="cp-sysconfig-row">
                                  <span className="cp-sysconfig-level-num">L{ol.level}</span>
                                  <span className="cp-sysconfig-name cp-sysconfig-name-bold">{ol.label}</span>
                                  <span className="cp-sysconfig-count">{ol.active_codes} active</span>
                                </div>
                                {ol.sample_values?.length > 0 && (
                                  <div className="cp-sysconfig-samples">
                                    {ol.sample_values.slice(0, 5).map((sv, j) => (
                                      <span key={j} className="cp-sysconfig-sample">{sv.description || sv.code}</span>
                                    ))}
                                    {ol.active_codes > 5 && <span className="cp-sysconfig-sample cp-sysconfig-more">+{ol.active_codes - 5} more</span>}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                    {(system_configuration.earning_groups?.length > 0 || system_configuration.deduction_groups?.length > 0) && (
                      <div className="cp-sysconfig-groups-row">
                        {system_configuration.earning_groups?.length > 0 && (
                          <div className="cp-sysconfig-group-block">
                            <span className="cp-sysconfig-group-label">Earning Groups</span>
                            <div className="cp-sysconfig-pills">
                              {system_configuration.earning_groups.map((eg, i) => (
                                <span key={i} className="cp-sysconfig-pill cp-sysconfig-pill-amber">{eg.code} <span className="cp-sysconfig-pill-count">{eg.employee_count.toLocaleString()}</span></span>
                              ))}
                            </div>
                          </div>
                        )}
                        {system_configuration.deduction_groups?.length > 0 && (
                          <div className="cp-sysconfig-group-block">
                            <span className="cp-sysconfig-group-label">Deduction Groups</span>
                            <div className="cp-sysconfig-pills">
                              {system_configuration.deduction_groups.map((dg, i) => (
                                <span key={i} className="cp-sysconfig-pill cp-sysconfig-pill-purple">{dg.code} <span className="cp-sysconfig-pill-count">{dg.employee_count.toLocaleString()}</span></span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                <div className="cp-section">
                  <div className="cp-section-header">
                    <div>
                      <div className="cp-section-title">Solution Matrix</div>
                      <div className="cp-section-subtitle">Registered vs available product keys</div>
                    </div>
                  </div>
                  <SolutionMatrix customerId={customerId} vendorLabel={identity?.hcm_product || identity?.hcm_vendor || ''} />
                </div>

                {complexity && (
                  <div className="cp-section">
                    <div className="cp-section-header">
                      <div>
                        <div className="cp-section-title">System Complexity</div>
                        <div className="cp-section-subtitle">What makes this setup complex to operate and keep compliant.</div>
                      </div>
                    </div>
                    <div className="cp-complexity">
                      {Object.entries(complexity).map(([key, cx]) => (
                        <div key={key} className="cp-cx-card">
                          <div className="cp-cx-label">{key.replace(/_/g, ' ')}</div>
                          <div className="cp-cx-value">{cx.label}</div>
                          <div className="cp-cx-detail">{cx.detail}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}

            {/* ── CONTRACT — as the customer sees it in the portal; admin detail inline ── */}
            {activeNav === 'contract' && (
              <ContractTab customerId={customerId} companyName={companyName} isAdmin={isAdmin} />
            )}

            {/* ── EMPLOYEE LIFELINE: the picker, or one person ── */}
            {activeNav === 'lifeline' && canViewEmployee && (
              lifelineFor
                ? <EmployeeLifeline key={lifelineFor} customerId={customerId} employeeRef={lifelineFor}
                                    onClose={closeEmployee} onPickAnother={pickAnother} />
                : <EmployeePicker customerId={customerId} onOpen={openEmployee} onClose={closeEmployee} />
            )}

            {/* ── CONSULTANT ONLY ── */}
            {activeNav === 'investigations' && (
              <WorkEffortsTab customerId={customerId} />
            )}

            {activeNav === 'data' && (
              <DataManagementTab customerId={customerId} />
            )}

            {activeNav === 'registers' && (
              <RegistersTab customerId={customerId} />
            )}

            {activeNav === 'compare' && (
              <div style={{ margin: '0 -24px', marginTop: -8 }}>
                <SchemaCompare embedded customerName={companyName} customerSystems={systems} />
              </div>
            )}

            {/* Footer */}
            <div style={{ textAlign: 'center', fontSize: 11, color: 'var(--gray-400)', marginTop: 24 }}>
              {profile.processing_ms}ms · {data_freshness.table_count} tables · {profile.fields_resolved ? Object.keys(profile.fields_resolved).length : 0} fields auto-detected
            </div>

          </div>{/* cpg-content-inner */}
          </div>
        </main>
      </div>

      {drillPanel && (
        <DrillPanel
          customerId={customerId}
          queryId={drillPanel.queryId}
          filters={drillPanel.filters}
          title={drillPanel.title}
          subtitle={drillPanel.subtitle}
          onClose={() => setDrillPanel(null)}
        />
      )}
    </div>
  )
}



